import React, { useState } from 'react';
import { MdCheck, MdArrowForward } from 'react-icons/md';
import { GuideIcon, parseGuideBody, renderInline } from './guideIcons';
import styles from './styles.module.css';

// Mirrors the app theme (lib/services/theme_services/app_colors.dart)
const THEMES = {
    light: { bg: '#F5F7FA', accent: '#18365E', text: '#1A1A1A', sub: '#6B7280' },
    dark: { bg: '#161A1D', accent: '#50B965', text: '#FFFFFF', sub: 'rgba(255,255,255,0.65)' },
};

// Hex + alpha → rgba() so tints follow the accent colour
const tint = (hex, alpha) => {
    const n = parseInt(hex.slice(1), 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

/**
 * Phone mock-up of one guide slide, matching the native app's UserGuideScreen:
 * step pill + Skip, large hero card, left-aligned title/body with check bullets,
 * dots + Next button.
 */
const GuidePhonePreview = ({ title, body, icon, imageUrl, mediaType = 'image', position = 1, total = 1 }) => {
    const [mode, setMode] = useState('dark');
    const [imageFailed, setImageFailed] = useState(false);
    const t = THEMES[mode];
    const isLast = position === total;
    const lines = parseGuideBody(body || 'Description text');

    // Reset error state when the URL changes
    const [lastUrl, setLastUrl] = useState(imageUrl);
    if (imageUrl !== lastUrl) {
        setLastUrl(imageUrl);
        setImageFailed(false);
    }

    const showImage = imageUrl && !imageFailed;

    return (
        <div>
            <div className={styles['preview-header']}>
                <span className={styles['preview-label']}>App preview</span>
                <div className={styles['mode-toggle']}>
                    {['light', 'dark'].map((m) => (
                        <button
                            key={m}
                            type="button"
                            className={mode === m ? styles['mode-active'] : ''}
                            onClick={() => setMode(m)}
                        >
                            {m === 'light' ? 'Light' : 'Dark'}
                        </button>
                    ))}
                </div>
            </div>

            <div className={styles.phone} style={{ background: t.bg }}>
                {/* Top bar */}
                <div className={styles['phone-top']}>
                    <span className={styles['phone-step']} style={{ background: tint(t.accent, 0.12), color: t.accent }}>
                        {position} / {total}
                    </span>
                    {!isLast && <span style={{ color: t.sub, fontWeight: 600 }}>Skip</span>}
                </div>

                {/* Hero card */}
                <div className={styles['phone-hero']}>
                    {showImage && mediaType === 'video' ? (
                        // Whole video at its own ratio, muted and looping — same as the app
                        <div className={styles['phone-hero-fit']}>
                            <video
                                key={imageUrl}
                                src={imageUrl}
                                className={styles['phone-hero-image']}
                                muted
                                loop
                                autoPlay
                                playsInline
                                onError={() => setImageFailed(true)}
                            />
                        </div>
                    ) : showImage ? (
                        // Whole image at its own ratio over a blurred copy — same as the app
                        <div className={styles['phone-hero-fit']}>
                            <img src={imageUrl} alt="" className={styles['phone-hero-backdrop']} aria-hidden />
                            <img src={imageUrl} alt="" className={styles['phone-hero-image']} onError={() => setImageFailed(true)} />
                        </div>
                    ) : (
                        <div
                            className={styles['phone-hero-art']}
                            style={{ background: `linear-gradient(135deg, ${tint(t.accent, 0.28)}, ${tint(t.accent, 0.06)})` }}
                        >
                            <span className={styles['phone-blob-a']} style={{ background: tint(t.accent, 0.10) }} />
                            <span className={styles['phone-blob-b']} style={{ background: tint(t.accent, 0.08) }} />
                            <span
                                className={styles['phone-hero-icon']}
                                style={{ background: t.accent, boxShadow: `0 8px 20px ${tint(t.accent, 0.35)}` }}
                            >
                                <GuideIcon name={icon} size={30} color="#fff" />
                            </span>
                        </div>
                    )}
                </div>

                {/* Content */}
                <div className={styles['phone-content']}>
                    <div className={styles['phone-title']} style={{ color: t.text }}>{title || 'Title'}</div>
                    <div className={styles['phone-body']}>
                        {lines.map((l, i) => l.bullet ? (
                            <div key={i} className={styles['phone-bullet']} style={{ color: t.text }}>
                                <span className={styles['phone-check']} style={{ background: tint(t.accent, 0.15), color: t.accent }}>
                                    <MdCheck size={9} />
                                </span>
                                <span>{renderInline(l.text, t.text)}</span>
                            </div>
                        ) : (
                            <div key={i} className={styles['phone-para']} style={{ color: t.sub }}>
                                {renderInline(l.text, t.text)}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Bottom bar */}
                <div className={styles['phone-bottom']}>
                    <div className={styles['phone-dots']}>
                        {Array.from({ length: total }).map((_, i) => (
                            <span
                                key={i}
                                style={{
                                    width: i === position - 1 ? 14 : 5,
                                    background: i === position - 1 ? t.accent : tint(t.accent, 0.25),
                                }}
                            />
                        ))}
                    </div>
                    <div className={styles['phone-actions']}>
                        {position > 1 && <span style={{ color: t.sub }}>Back</span>}
                        <span className={styles['phone-button']} style={{ background: t.accent }}>
                            {isLast ? 'Get started' : 'Next'}
                            {isLast ? <MdCheck size={12} /> : <MdArrowForward size={12} />}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GuidePhonePreview;
